import React, { useState } from 'react';
import { 
  FlaskConical, 
  TrendingUp, 
  Brain, 
  Shield,
  Map,
  AlertTriangle
} from 'lucide-react';
import MonteCarloSimulation from './MonteCarloSimulation';
import MarketIntelligence from './MarketIntelligence';
import AIRentPredictor from './AIRentPredictor';
import RiskAnalytics from './RiskAnalytics';
import { useExperimentalFeatures } from '../../hooks/useExperimentalFeatures';

interface ExperimentalFeature {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'active' | 'beta' | 'alpha' | 'planned';
  component: React.ComponentType;
  enabled: boolean;
}

const ExperimentalFeatures: React.FC = () => {
  const { toggleFeature, isFeatureEnabled } = useExperimentalFeatures();
  const [activeFeature, setActiveFeature] = useState<string>('overview');

  const features: ExperimentalFeature[] = [
    {
      id: 'monte-carlo',
      name: 'Monte Carlo Simulation',
      description: 'Erweiterte Risikoanalyse mit 10.000+ Szenarien',
      icon: <TrendingUp className="w-5 h-5" />,
      status: 'active',
      component: MonteCarloSimulation,
      enabled: isFeatureEnabled('monte-carlo')
    },
    {
      id: 'market-intelligence',
      name: 'Market Intelligence',
      description: 'Echtzeit-Marktdaten und Vergleichsanalysen',
      icon: <Map className="w-5 h-5" />,
      status: 'beta',
      component: MarketIntelligence,
      enabled: isFeatureEnabled('market-intelligence')
    },
    {
      id: 'ai-rent-predictor',
      name: 'KI Mietprognose',
      description: 'Machine Learning basierte Mietpreisvorhersage',
      icon: <Brain className="w-5 h-5" />,
      status: 'alpha',
      component: AIRentPredictor,
      enabled: isFeatureEnabled('ai-rent-predictor')
    },
    {
      id: 'risk-analytics',
      name: 'Erweiterte Risikoanalyse',
      description: 'Portfolio VaR und Stress-Testing',
      icon: <Shield className="w-5 h-5" />,
      status: 'beta',
      component: RiskAnalytics,
      enabled: isFeatureEnabled('risk-analytics')
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'beta': return 'bg-yellow-100 text-yellow-800';
      case 'alpha': return 'bg-orange-100 text-orange-800';
      case 'planned': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
          <div>
            <h3 className="font-semibold text-yellow-900">Experimentelle Features</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Diese Features befinden sich in aktiver Entwicklung und können sich ändern. 
              Nutzen Sie sie mit Vorsicht in produktiven Umgebungen.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature) => (
          <div 
            key={feature.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{feature.name}</h3>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${getStatusColor(feature.status)}`}>
                    {feature.status.toUpperCase()}
                  </span>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={feature.enabled}
                  onChange={() => toggleFeature(feature.id as any)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
            {feature.enabled && (
              <button
                onClick={() => setActiveFeature(feature.id)}
                className="w-full px-3 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
              >
                Feature öffnen
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Kommende Features</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-center">
            <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
            Blockchain-Integration für Verträge
          </li>
          <li className="flex items-center">
            <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
            AR Immobilienbesichtigung
          </li>
          <li className="flex items-center">
            <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
            Voice Interface (Alexa/Google)
          </li>
          <li className="flex items-center">
            <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
            WebAssembly für Performance
          </li>
        </ul>
      </div>
    </div>
  );

  const renderActiveFeature = () => {
    const feature = features.find(f => f.id === activeFeature);
    if (!feature || !feature.enabled) return null;
    
    const Component = feature.component;
    return (
      <div className="space-y-4">
        <button
          onClick={() => setActiveFeature('overview')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          ← Zurück zur Übersicht
        </button>
        <Component />
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <FlaskConical className="w-8 h-8 text-indigo-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Experimental Lab</h1>
        </div>
        <p className="text-gray-600">
          Testen Sie die neuesten Features und Innovationen für Immobilien-Analyzer Pro
        </p>
      </div>

      {activeFeature === 'overview' ? renderOverview() : renderActiveFeature()}
    </div>
  );
};

export default ExperimentalFeatures;